import { http } from "@/services/http";
import qs from "qs";
import { cloneDeep } from "lodash";
import {
  mapDriverFilterQueryParameters,
  mapDriverFilters,
  mapDriverFilterState,
} from "@/utils/DriverFilterMapping";
import { mapModules } from "@/utils/ModuleMapping";
import { driverColumnDefinition } from "@/utils/ColumnMapping";
import { formatNumber } from "@/utils/FormatUtils";
import { unsetFilters } from "@/utils/FilterUtils";
import saveAs from "file-saver";

/**
 * Store for the state of (led) modules
 *
 * @type Object
 */
const state = {
  inputLock: "current",
  latestChange: "",
  driverCurrent: null,
  driverVoltage: null,
  driverPower: null,
  driverPerformancePower: null,
  driverSearchTerm: null,
  driverFamilies: [],
  filters: [],
  rawFilters: [],
  drivers: [],
  driver: null,
  driverFilters: null,
  initialFilters: [],
  driverColumnVisibility: [],
  pinnedDrivers: [],
  comparedDrivers: [],
  columnDefs: [],
  isOddCombination: false,
  isWhitelistedSolution: true,
  lastDriverCalculationParameters: {},
};

const getters = {
  initialColumnDefs: (state, getters, rootState) => {
    return driverColumnDefinition(rootState.brain.region);
  },
  columnDefs: (state) => {
    return state.columnDefs;
  },
  emptyInitialFilters(state) {
    return unsetFilters(state.initialFilters);
  },
  rawFilters: (state) => {
    return state.rawFilters;
  },
  pinnedDrivers: (state) => {
    return state.pinnedDrivers;
  },
  comparedDrivers: (state) => {
    return state.comparedDrivers;
  },
  driver: (state) => {
    return state.driver;
  },
  drivers: (state) => {
    return state.drivers;
  },
  driverCurrent: (state) => {
    return state.driverCurrent;
  },
  driverVoltage: (state) => {
    return state.driverVoltage;
  },
  driverPower: (state) => {
    return state.driverPower;
  },
  isOddCombination: (state) => {
    return state.isOddCombination;
  },
  isWhitelistedSolution: (state) => {
    return state.isWhitelistedSolution;
  },
};

const mutations = {
  setColumnDefs: (state, columnDefs) => {
    state.columnDefs = [...columnDefs];
  },

  setFilters: (state, filters) => {
    if (filters) {
      state.filters = [...filters];
    }
  },

  setRawFilters: (state, rawFilters) => {
    state.rawFilters = rawFilters;
  },

  setDriverSearchTerm: (state, searchTerm) => {
    state.driverSearchTerm = searchTerm;
  },
  resetSpecificFilterGroup: (state, specificFilterGroup) => {
    const targetInitialFilterGroupIndex = state.initialFilters.findIndex(
      (initialFilter) => initialFilter.title === specificFilterGroup.title
    );
    state.filters[targetInitialFilterGroupIndex] = specificFilterGroup;
  },

  updateFilter: (state, { update, showArchive }) => {
    state.filters.forEach((group) => {
      group.filters.forEach((filter) => {
        if (
          !showArchive &&
          filter.name === "releaseState" &&
          filter.value &&
          filter.value.includes("Archived")
        ) {
          filter.value = filter.value.filter((f) => f !== "Archived");
        }

        if (update && filter.name === update.name) {
          if (
            filter.name === "driverHousingWidth" ||
            filter.name === "driverHousingLength" ||
            filter.name === "driverHousingHeight" ||
            filter.name === "vpk"
          ) {
            filter.value = {
              min: update.min,
              max: update.max,
            };
          } else if (filter.name === "searchTermFilter") {
            state.driverSearchTerm = update.value;
            filter.value = update.value;
          } else {
            filter.value =
              update.values instanceof Array
                ? [...update.values]
                : update.values;
          }
        }
      });
    });
  },

  setInitialFilters: (state, filters) => {
    state.initialFilters = filters;
  },

  setDrivers: (state, drivers) => {
    state.drivers = [...drivers];
  },

  setDriverFamilies: (state, driverFamilies) => {
    state.driverFamilies = [...driverFamilies];
  },

  setDriverCurrent: (state, current) => {
    state.driverCurrent = current;
    state.latestChange = "current";
  },

  setDriverVoltage: (state, voltage) => {
    state.driverVoltage = voltage;
    state.latestChange = "voltage";
  },

  setDriverPower: (state, power) => {
    state.driverPower = power;
    state.latestChange = "power";
  },

  setDriverPerformancePower: (state, performancePower) => {
    state.driverPerformancePower = performancePower;
  },

  adjustDriverPower: (state) => {
    // Can't do adjustment with just one values
    if (
      ["driverCurrent", "driverVoltage", "driverPower"].filter((name) =>
        isValid(state[name])
      ).length < 2
    ) {
      return;
    }

    if (
      isValid(state.driverPower) &&
      isValid(state.driverCurrent) &&
      isValid(state.driverVoltage)
    ) {
      // All values available, use inputLock + last changed field
      if (state.inputLock === "current") {
        // Current is locked
        if (state.latestChange !== "voltage") {
          // Calculate voltage
          state.driverVoltage = formatNumber(
            state.driverPower / (state.driverCurrent / 1000),
            "0.0"
          );
        }
      } else {
        // Voltage is locked
        if (state.latestChange !== "current") {
          // Calculate current
          state.driverCurrent = formatNumber(
            (state.driverPower * 1000) / state.driverVoltage,
            "0"
          );
        }
      }
      if (state.latestChange !== "power") {
        // Calculate power inputLock does not matter here
        state.driverPower = formatNumber(
          (state.driverCurrent / 1000) * state.driverVoltage,
          "1"
        );
      }
    } else {
      // Not all values are available, calculate empty field inputLock does not matter
      if (!isValid(state.driverCurrent)) {
        //Current is empty
        state.driverCurrent = formatNumber(
          (state.driverPower * 1000) / state.driverVoltage,
          "0"
        );
      }
      if (!isValid(state.driverVoltage)) {
        //Voltage is empty
        state.driverVoltage = formatNumber(
          state.driverPower / (state.driverCurrent / 1000),
          "0.0"
        );
      }
      if (!isValid(state.driverPower)) {
        //Power is empty
        state.driverPower = formatNumber(
          (state.driverCurrent / 1000) * state.driverVoltage,
          "1"
        );
      }
    }
  },
  saveDriverCalculationParameters: (state) => {
    state.lastDriverCalculationParameters = {
      driverCurrent: state.driverCurrent,
      driverVoltage: state.driverVoltage,
      driverPower: state.driverPower,
      driverPerformancePower: state.driverPerformancePower,
    };
  },
  resetDriverCalculationParameters: (state) => {
    ({
      driverCurrent: state.driverCurrent,
      driverVoltage: state.driverVoltage,
      driverPower: state.driverPower,
      driverPerformancePower: state.driverPerformancePower,
    } = state.lastDriverCalculationParameters);
  },
  setInputLock: (state, lock) => {
    state.inputLock = lock;
  },
  setDriver: (state, driver) => {
    state.driver = cloneDeep(driver);
    state.driverFilters = cloneDeep(state.filters);
    mutations.saveDriverCalculationParameters(state);
  },
  setInputForm: (state, module) => {
    if (module != null) {
      state.driverVoltage = formatNumber(
        module.calculationResult.outputVoltageOfArray,
        "0.0"
      );
      // Convert from Amps to milliAmps
      state.driverCurrent = formatNumber(
        module.calculationResult.operatingCurrent * 1000,
        "0"
      );
      state.driverPower = formatNumber(module.calculationResult.power, "0.0");
    }
  },
  clearCalculatedPerformance: (state) => {
    state.driverPower = null;
    state.driverCurrent = null;
    state.driverVoltage = null;
    state.driverPerformancePower = null;
    state.latestChange = null;
    state.inputLock = "";
  },
  setPinnedDrivers: (state, pinnedDrivers) => {
    state.pinnedDrivers = pinnedDrivers;
  },
  pinDriver: (state, driver) => {
    state.pinnedDrivers.push(cloneDeep(driver));
  },
  unpinDriver: (state, driver) => {
    const index = state.pinnedDrivers.findIndex((d) => d.uid === driver.uid);
    if (index !== -1) {
      state.pinnedDrivers.splice(index, 1);
    }
  },
  setComparedDrivers: (state, comparedDrivers) => {
    state.comparedDrivers = [...comparedDrivers];
  },
  addToComparison: (state, driver) => {
    if (!state.comparedDrivers.some((d) => d.uid === driver.uid)) {
      state.comparedDrivers.push(cloneDeep(driver));
    }
  },
  removeFromComparison: (state, driver) => {
    const index = state.comparedDrivers.findIndex((d) => d.uid === driver.uid);
    if (index !== -1) {
      state.comparedDrivers.splice(index, 1);
    }
  },
  setIsOddCombination: (state, isOddCombination) => {
    state.isOddCombination = isOddCombination;
  },
  setIsWhitelistedSolution: (state, isWhitelistedSolution) => {
    state.isWhitelistedSolution = isWhitelistedSolution;
  },
};

const actions = {
  async getDrivers({ rootState, rootGetters, commit, dispatch }) {
    commit("brain/global/setBusy", true, { root: true });

    // If a solution is chosen use it in the headers
    dispatch("brain/solutions/setHeaders", null, { root: true });

    //The 'filters' are used to be send in the REQUEST
    const filters = await mapDriverFilterState(state);
    const result = await http
      .post(
        "/api/v2/drivers/data" +
          mapDriverFilterQueryParameters(rootGetters["brain/global/filters"]),
        qs.stringify(filters, { indices: false, arrayFormat: "brackets" }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      )
      .then((response) => response.data.data)
      .then((data) => {
        const mappedFilters = mapDriverFilters(
          rootState.brain.region,
          data.filters
        );
        // Set Filters
        commit("setFilters", mappedFilters);

        // Set Raw Filters
        commit("setRawFilters", data.filters);

        dispatch("brain/global/setTagFamilies", null, { root: true });

        if (!state.initialFilters.length) {
          commit("setInitialFilters", mappedFilters);
        }

        // The whole frontend is prepared for Output Current given in mA, but the API gives it in A, so we convert
        // accordingly from the get-go
        for (let hit of data.hits) {
          hit.ocMax /= 1000;
          hit.ocMin /= 1000;
        }

        const drivers = mapModules(rootState.brain.region, data.hits);
        commit("setDrivers", drivers);

        if (state.driver !== null) {
          const activeDriver = drivers.find(
            (driver) => driver.uid === state.driver.uid
          );
          if (activeDriver !== undefined) {
            commit("setDriver", activeDriver);
          } else {
            commit("setDriver", null);
          }
        }
        if (rootGetters["brain/solutions/hasSolutions"]) {
          const solutionDriver = rootGetters["brain/solutions/solution"].driver;
          if (solutionDriver) {
            const selectedDriver = drivers.find(
              (driver) => driver.uid === solutionDriver.uid
            );
            if (selectedDriver !== undefined) {
              dispatch(
                "brain/solutions/add",
                { type: "driver", addNew: false, data: selectedDriver },
                { root: true }
              );
            } else {
              dispatch(
                "brain/solutions/removeDriver",
                rootState.brain.solutions.active,
                { root: true }
              );
              this._vm.$snotify["error"](
                "Selected driver cannot work with current system, please select a new driver."
              );
            }
          }
        }

        return drivers;
      })
      .catch((err) => {
        commit("brain/global/setBusy", false, { root: true });
        if (err.response !== undefined && err.response.status === 401) {
          this._vm.$snotify["error"]("Token expired, please log in again.");
        } else {
          this._vm.$snotify["error"](err);
        }
      });

    // clear busy flag
    commit("brain/global/setBusy", false, { root: true });

    return result;
  },

  async getAllDriverFamilies({ commit }) {
    const result = await http.get(`/api/v2/driverfamily/all`);
    commit("setDriverFamilies", result.data.data);
  },

  async getAllReadableDriverFamilies({ commit }) {
    const result = await http.get(`/api/v2/driverfamily/all-readable`);
    commit("setDriverFamilies", result.data.data);
  },

  async handleFilterChange({ dispatch, commit, rootGetters }, { data }) {
    commit("updateFilter", {
      update: data,
      showArchive: rootGetters["brain/global/showArchive"],
    });
    return await dispatch("getDrivers");
  },

  async resetAllFilters({ dispatch }) {
    await dispatch("resetFilters");
    return await dispatch("getDrivers");
  },

  async resetFilters({ commit, rootState }) {
    commit("setDriverSearchTerm", null);
    const rawDriverFilters = await http({
      method: "POST",
      url: "/api/v2/drivers/data?hasMatchingDriver=true&whitelistingEnabled=true&showArchive=false&size=0",
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then((response) => {
      return response.data.data.filters;
    });
    commit(
      "setFilters",
      mapDriverFilters(rootState.brain.region, rawDriverFilters)
    );
  },

  async checkSensorWhitelist({ commit }, data) {
    commit("brain/global/setBusy", true, { root: true });
    let formData = new FormData();
    formData.append("sensorNc12", data.sensorNc12);
    formData.append("driverNc12", data.driverNc12);
    return http({
      method: "POST",
      url: "/api/v2/whitelisted-combination",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then((res) => {
        commit("brain/global/setBusy", false, { root: true });
        return res;
      })
      .catch((err) => {
        commit("brain/global/setBusy", false, { root: true });
        console.log(err);
        return err;
      });
  },

  async downloadDrivers({ rootGetters, state, commit }) {
    commit("brain/global/setBusy", true, { root: true });
    // create the filterset to apply
    const filters = mapDriverFilterState(state);

    // and request the list
    const result = await http
      .post(
        "api/v2/drivers/exportxlsx" +
          mapDriverFilterQueryParameters(rootGetters["brain/global/filters"]),
        qs.stringify(filters, { indices: false, arrayFormat: "brackets" }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          responseType: "arraybuffer",
        }
      )
      .then((response) => {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;",
        });
        const date = new Date();
        const filename = "drivers-export-" + date.getTime();

        saveAs(blob, filename + ".xlsx");
      })
      .catch((err) => {
        if (err.response !== undefined && err.response.status === 401) {
          this._vm.$snotify["error"]("Token expired, please log in again.");
        } else {
          this._vm.$snotify["error"](err);
        }
      });
    commit("brain/global/setBusy", false, { root: true });
    return result;
  },

  async setNewFilterSet({ rootState, rootGetters, commit }) {
    const filters = mapDriverFilterState(state);
    await http
      .post(
        "/api/v2/drivers/data" +
          mapDriverFilterQueryParameters(rootGetters["brain/global/filters"]) +
          "&size=0",
        qs.stringify(filters, { indices: false, arrayFormat: "brackets" }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      )
      .then((response) => response.data.data)
      .then((data) => {
        // Set Filters
        commit(
          "setFilters",
          mapDriverFilters(rootState.brain.region, data.filters)
        );

        // Set Raw Filters
        commit("setRawFilters", data.filters);
      });
  },

  setFilters({ commit }, filters) {
    commit("setFilters", filters);
    const searchFilter = filters.find(
      (filter) => filter.title === "Exceptions"
    );
    if (searchFilter) {
      if (searchFilter.filters[0].value != null) {
        commit("setDriverSearchTerm", searchFilter.filters[0].value);
      }
    }
  },
  fetchIsOddCombination({ commit }) {
    return http.get("/api/v2/drivers/isOddCombination").then((response) => {
      const isOddCombination = response.data.data;
      commit("setIsOddCombination", isOddCombination);
      return isOddCombination;
    });
  },
  fetchIsWhitelistedSolution({ commit }) {
    return http
      .get("/api/v2/drivers/isWhitelistedSolution")
      .then((response) => {
        const isWhitelistedSolution = response.data.data;
        commit("setIsWhitelistedSolution", isWhitelistedSolution);
        return isWhitelistedSolution;
      });
  },
};

function isValid(value) {
  return !isNaN(value) && value > 0;
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
